import {
  Component,
  EventEmitter,
  inject,
  InjectFlags,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Option, UserSkillModel } from '@digilize/shared/definitions/src';
import { SkillType } from '@digilize/shared/definitions/src/lib/enums';
import { SingleSelectSearchComponent } from '@digilize/shared/ui/components/src/lib/form-items/single-select-search/single-select-search.component';
import { debounceTime, distinctUntilChanged, forkJoin, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';

import { createOptions } from '../../../helpers/helpers';
import { ProfessionsService } from '../../../services/professions.service';
import { SkillsService } from '../../../services/skills.service';
import { WizardService } from '../../../services/wizard.service';

@Component({
  selector: 'bwc-search-add-skills',
  templateUrl: './search-add-skills.component.html',
  styleUrls: ['./search-add-skills.component.scss'],
})
export class SearchAddSkillsComponent implements OnInit, OnDestroy {
  @Input() prevBut: boolean;
  @Input() closeBut: boolean;
  @Input() editing: boolean;
  @Input() disableNext = false;
  @Input() excludeSkillType: SkillType[] = [SkillType.Language];
  @Input() sectionTitle = '';
  @Output() emitClose = new EventEmitter();
  @Output() emitSkills = new EventEmitter();
  @ViewChild('searchAutocomplete') searchAutocomplete: SingleSelectSearchComponent;
  @ViewChild('professionSearchAutocomplete') professionSearchAutocomplete: SingleSelectSearchComponent;

  wizardService = inject(WizardService, InjectFlags.Optional);

  form: FormGroup;
  professionOptions: Option[] = [];
  myProfessionTags: Option[] = [];
  professionSkills: Option[] = [];
  searchSkills: Option[] = [];
  dataLoaded = false;
  professionSkillsLoaded = false;
  selectedTabIndex = 0;
  searchLength = 25;
  searchValue = '';
  searchSkillCustom = false;
  saving = false;

  selectedProfession: Option | null = null;
  private existingSkillIds: string[] = [];

  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private readonly fb = inject(FormBuilder);
  private readonly skillsService = inject(SkillsService);

  private readonly professionsService = inject(ProfessionsService);

  ngOnInit(): void {
    this.form = this.createFormGroup();
    this.getData();
    this.initSearchSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createFormGroup(): FormGroup {
    return this.fb.group({
      addedSkills: this.fb.array([]),
    });
  }

  private getData(): void {
    forkJoin({
      professions: this.professionsService.getListOfProfessions(''),
      existingSkills: this.skillsService.getAllUserSkills(),
      myProfessions: this.professionsService.getProfessionsMy(),
    }).subscribe((data) => {
      this.professionOptions = createOptions(data.professions.data);

      if (data.existingSkills?.data?.length > 0) {
        this.existingSkillIds = data.existingSkills.data.map((item) => item.skill_id).filter(Boolean);
      }

      this.myProfessionTags = (data.myProfessions?.data ?? [])
        .filter((w) => w.position_id && w.position_name)
        .filter((w, i, arr) => arr.findIndex((x) => x.position_id === w.position_id) === i)
        .map((w) => ({ id: w.position_id, text: w.position_name } as Option));

      this.dataLoaded = true;
    });
  }

  handleProfessionSearchValueChanged(value: string): void {
    this.professionsService.getListOfProfessions(value).subscribe((data) => {
      this.professionOptions = createOptions(data.data);
    });
  }

  handleProfessionSelected(option: Option): void {
    this.selectedProfession = option;
    this.professionSkillsLoaded = false;
    this.loadSkillsForProfession();
  }

  handleMyProfessionTagClick(tag: Option): void {
    this.handleProfessionSelected(tag);
  }

  clearSelectedProfession(): void {
    this.selectedProfession = null;
    this.professionSkills = [];
    this.professionSkillsLoaded = false;
  }

  private loadSkillsForProfession(): void {
    if (!this.selectedProfession) {
      return;
    }

    const obj = {
      searchValue: '',
      start: 0,
      length: this.searchLength,
      id: this.selectedProfession.id,
      excludeIds: this.getAllExcludeIds(),
      excludeSkillType: this.excludeSkillType,
    };
    this.skillsService.searchForSkills(obj).subscribe((skills) => {
      this.professionSkills = this.removeDuplicates(createOptions(skills.data), this.addedSkills.value);
      this.professionSkillsLoaded = true;
    });
  }

  toggleProfessionSkill(skill: Option): void {
    const existingIndex = this.addedSkills.controls.findIndex((c) => c.value.skill_id === skill.id);
    if (existingIndex !== -1) {
      this.addedSkills.removeAt(existingIndex);
    } else {
      this.addedSkills.insert(
        0,
        this.fb.control({
          skill_id: skill.id,
          skill_name: skill.text,
          rating: 0,
        })
      );
    }
  }

  isSkillAdded(skill: Option): boolean {
    return (
      this.existingSkillIds.includes(skill.id) || this.addedSkills.controls.some((c) => c.value.skill_id === skill.id)
    );
  }

  handleSearchSkillClick(option: Option): void {
    this.addedSkills.insert(
      0,
      this.fb.control({
        skill_id: option.id,
        skill_name: option.text,
        rating: 0,
      })
    );
    this.searchSkillCustom = false;
  }

  handleSearchValueChanged(value: string): void {
    this.searchInput$.next(value);
  }

  private initSearchSubscription(): void {
    this.searchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => {
          this.searchValue = value;
          const obj = {
            searchValue: value,
            start: 0,
            length: this.searchLength,
            excludeIds: this.getAllExcludeIds(),
            excludeSkillType: this.excludeSkillType,
          };
          return this.skillsService.searchForSkills(obj);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((skills) => {
        this.searchSkills = this.removeDuplicates(createOptions(skills.data), this.addedSkills.value);
        this.searchSkillCustom = false;
      });
  }

  handleScrolledToEnd(): void {
    const obj = {
      searchValue: this.searchValue,
      start: this.searchSkills.length,
      length: this.searchLength,
      excludeIds: this.getAllExcludeIds(),
      excludeSkillType: this.excludeSkillType,
    };
    this.skillsService.searchForSkills(obj).subscribe((skills) => {
      const newOptions = createOptions(skills.data);
      this.searchSkills = [...this.searchSkills, ...newOptions];
    });
  }

  toggleSkillActive(index: number): void {
    this.addedSkills.removeAt(index);
  }

  goPrev(): void {
    this.saveSkillsData(false, true);
  }

  onSubmit(): void {
    this.saveSkillsData();
  }

  onEdit(): void {
    this.saveSkillsData(true);
  }

  onCancel(): void {
    this.emitClose.emit('cancelled');
  }

  private saveSkillsData(close?: boolean, ifBack?: boolean): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.commitPendingSkills().subscribe({
      next: () => {
        this.saving = false;
        if (ifBack) {
          if (this.wizardService) {
            this.wizardService.goToPrevious();
          } else {
            this.emitClose.emit('search_add_skills');
          }
        } else if (close) {
          this.emitSkills.emit(this.addedSkills);
          this.emitClose.emit('search_add_skills');
        } else {
          this.wizardService?.goToNext();
        }
      },
      error: () => {
        this.saving = false;
      },
    });
  }

  private commitPendingSkills(): Observable<unknown> {
    const reqs = this.addedSkills.controls.map((c) =>
      this.skillsService.addUserSkill(this.buildUserSkillReq(c.value.skill_id, c.value.skill_name))
    );
    return reqs.length ? forkJoin(reqs) : of(null);
  }

  private buildUserSkillReq(skillId: string | null, skillName: string): UserSkillModel {
    return {
      id: null as unknown as string,
      skill_id: skillId,
      skill_name: skillName,
      skill_mapping_name: null,
      rating: 0,
      rating_written: null,
      is_native: null,
      type: SkillType.Unknown,
      order: 0,
    };
  }

  close(): void {
    this.emitClose.emit('search_add_skills');
  }

  get addedSkills(): FormArray {
    return this.form.get('addedSkills') as FormArray;
  }

  get filteredSearchSkills(): Option[] {
    const excludeIds = new Set(this.getAllExcludeIds());
    return this.searchSkills.filter((s) => !excludeIds.has(s.id));
  }

  private getAllExcludeIds(): string[] {
    const addedIds = this.addedSkills.controls.map((c) => c.value.skill_id).filter(Boolean);
    return [...new Set([...addedIds, ...this.existingSkillIds])];
  }

  private removeDuplicates(available: Option[], added: any[]): Option[] {
    const addedIds = added.map((s) => s.skill_id || s.id);
    return available.filter((s) => !addedIds.includes(s.id));
  }
}
